import axios from 'axios';
import { TripData } from '../pages/AccountPage';
import { auth } from '../firebase/config';

// Type for saving new trips where ID is optional
export type NewTripData = Omit<TripData, 'id'>;

// src/utils/api.ts
export interface GeminiResponse {
  travelCompanions?: string;
  budget?: string;
  accommodation?: string[];
  [key: string]: string | string[] | undefined;
}

export interface CountryAnalysis {
  recommendations: Array<{
    name: string;
    reason: string;
  }>;
}

export interface TravelSuggestion {
  tripDetails: {
    startDate: string;
    endDate: string;
    selectedCountries: string[];
    description: string;
    aiAssistData?: any;
  };
  selectedCities: Array<{
    id: string;
    name: string;
    country: string;
    image: string | undefined;
    coordinates: {
      lat: number;
      lng: number;
    };
  }>;
  daysArray: number[];
  cityCosts: Record<string, any>;
  totalTripCost: number;
}

export interface TransitRouteReference {
  fromCity: string;
  toCity: string;
  ref: string;
}

// Helper function to sanitize route data before sending to API
function sanitizeRouteData(routeData: any): any {
  if (!routeData) return {};
  
  try {
    // Create a deep copy to avoid modifying the original
    const sanitized = JSON.parse(JSON.stringify(routeData));
    
    // Remove undefined values (they can cause issues in API requests)
    Object.keys(sanitized).forEach(key => {
      if (sanitized[key] === undefined) {
        delete sanitized[key];
      }
    });
    
    // Handle the routeDetails object specially to avoid potential circular references
    if (sanitized.routeDetails) {
      try {
        // Test if it can be stringified without error
        JSON.stringify(sanitized.routeDetails);
      } catch (error) {
        // If there's an error, replace with simplified version
        sanitized.routeDetails = {
          duration: sanitized.routeDetails.duration || 
                   sanitized.routeDetails?.localizedValues?.duration?.text,
          distance: sanitized.routeDetails.distance || 
                   sanitized.routeDetails?.localizedValues?.distance?.text,
          summary: sanitized.routeDetails.summary || 
                  sanitized.routeDetails?.localizedValues?.instructions
        };
      }
    }
    
    // If polyline is array of undefined, convert to empty array
    if (Array.isArray(sanitized.polyline) && 
        sanitized.polyline.every((item: unknown) => item === undefined)) {
      sanitized.polyline = [];
    }
    
    return sanitized;
  } catch (error) {
    console.error('Error sanitizing route data:', error);
    // Return a minimal valid object
    return {
      fromCity: routeData.fromCity || '',
      toCity: routeData.toCity || '',
      duration: routeData.duration || ''
    };
  }
}


const API_BASE_URL = process.env.NODE_ENV === 'production' 
? 'https://travelapp-bens.onrender.com' 
: 'http://localhost:5001';

const allowedOrigins = process.env.NODE_ENV === 'production'
  ? ['https://your-vercel-domain.vercel.app', 'https://trekovatravel.com']
  : ['http://localhost:5001'];

// Token cache in module scope
let csrfTokenCache: string | null = null;
let tokenPromise: Promise<string> | null = null;

export const api = {
  get: async <T>(endpoint: string, options?: { params?: Record<string, any> }): Promise<T> => {
    const response = await secureAxios.get(endpoint, options);
    return response.data;
  },

  post: async <T>(endpoint: string, data?: any): Promise<T> => {
    const response = await secureAxios.post(endpoint, data);
    return response.data;
  }
};

export const tripApi = {
  analyzeTrip: (data: any) => 
    api.post<CountryAnalysis>('/api/gemini/analyze', data),
    
  generateTrip: (preferences: any) => 
    api.post<TravelSuggestion>('/api/gemini/generate-trip', preferences),

  generateTripsInBatch: (preferences: any, countries: string[]) => 
    api.post<{ trips: TravelSuggestion[] }>('/api/gemini/generate-trips-batch', { 
      preferences,
      countries 
    }),
    
  getPreferences: (prompt: string) => 
    api.post<GeminiResponse>('/api/gemini/preferences', { prompt }),

  generateSurpriseTrip: (filters: {
    region?: string;
    climate?: string;
    budget?: string;
    other?: string;
    startDate: string;
    endDate: string;
  }) =>
  api.post<TravelSuggestion>('/api/gemini/generate-surprise-trip', filters),

  saveTrip: async (userId: string, tripData: NewTripData) =>
  api.post<{ id: string }>(`/api/users/${userId}/trips`, tripData),
  
  getTrip: (userId: string, tripId: string) =>
    api.get<TripData>(`/api/users/${userId}/trips/${tripId}`),
    
  updateTrip: async (userId: string, tripId: string, tripData: Partial<TripData>) => {
    if (!tripId || tripId === 'undefined') {
      console.error('Invalid tripId when updating trip:', tripId);
      throw new Error('Cannot update trip with invalid tripId');
    }
    
    try {
      // Sanitize tripData to remove any undefined values
      const sanitizedData = JSON.parse(JSON.stringify(tripData));
      
      // Always try to update first since we know we have a valid tripId
      return api.post<{ id: string }>(`/api/users/${userId}/trips/${tripId}`, sanitizedData);
    } catch (error) {
      console.error('Error updating trip:', error);
      throw error; // Let the caller handle the error
    }
  },
  
  getAllTrips: async (userId: string): Promise<TripData[]> => {
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://travelapp-bens.onrender.com' 
      : 'http://localhost:5001';
      
    try {
      // Get fresh Firebase token
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }
      
      const idToken = await user.getIdToken(true); // Force refresh
      
      const response = await axios.get<TripData[]>(`${baseUrl}/api/users/${userId}/trips`, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to fetch trips:', {
          status: error.response?.status,
          data: error.response?.data,
          headers: error.response?.headers
        });
      }
      throw error;
    }
  },
    
  // Replace your current deleteTrip function with this version
  // No need for complex client-side logic since the server handles it all
  deleteTrip: (userId: string, tripId: string) =>
    api.post<void>(`/api/users/${userId}/trips/${tripId}/delete`),

  // Share endpoints
  createShareLink: (userId: string, tripId: string, expiresInDays = 30) =>
    api.post<{ token: string; shareUrl: string; expiresAt: number }>(`/api/users/${userId}/trips/${tripId}/share-link`, { expiresInDays }),

  shareWithUser: (userId: string, tripId: string, emailOrUid: string, role: 'viewer' | 'editor' = 'viewer') =>
    api.post<{ success: boolean }>(`/api/users/${userId}/trips/${tripId}/share-with`, { emailOrUid, role }),

  getSharedWithMe: async (): Promise<TripData[]> => {
    return api.get<TripData[]>(`/api/trips/shared-with-me`);
  },

  getSharedTripByToken: async (token: string) => {
    return api.get<TripData>(`/api/shared/trips/${token}`);
  },

  saveTransitRoute: async (userId: string, tripId: string, routeKey: string, routeData: any) => {
    console.log(`Saving route at path: users/${userId}/trips/${tripId}/routes/${routeKey}`);
    if (!tripId || tripId === 'undefined') {
      console.error('Invalid tripId when saving route:', tripId);
      throw new Error('Cannot save route with invalid tripId');
    }
    
    // Sanitize route data before sending to API
    const sanitizedData = sanitizeRouteData(routeData);
    
    return api.post<{ id: string }>(
      `/api/users/${userId}/trips/${tripId}/routes/${routeKey}`,
      sanitizedData
    );
  },
  
  // Get a specific transit route
  getTransitRoute: async (userId: string, tripId: string, routeKey: string) => {
    try {
      console.log(`Url being tested: /api/users/${userId}/trips/${tripId}/routes/${routeKey}`)
      return await api.get<any>(`/api/users/${userId}/trips/${tripId}/routes/${routeKey}`);
    } catch (error) {
      console.log(`Route not found at specified path: ${routeKey}`);
      // Return null to indicate route not found, allowing fallback logic
      return null;
    }
  },
  
  // Update the main trip document with route references
  updateTripRouteReferences: async (userId: string, tripId: string, routeReferences: any) => {
    return api.post<void>(
      `/api/users/${userId}/trips/${tripId}`,
      { transitRoutes: routeReferences }
    );
  },
  
  // Load a complete trip with all its routes
  loadTripWithRoutes: async (userId: string, tripId: string): Promise<TripData> => {
    try {
      // Get the main trip data
      console.log(`Loading trip data for: ${userId}/${tripId}`);
      const tripData = await api.get<TripData>(`/api/users/${userId}/trips/${tripId}`);
      console.log("Main trip data loaded successfully");
      
      // Check if routes exist at all
      if (!tripData.transitRoutes || Object.keys(tripData.transitRoutes).length === 0) {
        console.log("No transit routes found in trip data");
        return tripData;
      }
      
      const routeKeys = Object.keys(tripData.transitRoutes);
      console.log("Found route keys:", routeKeys);
      
      // Check if routes are already embedded
      const firstRouteKey = routeKeys[0];
      const firstRoute = tripData.transitRoutes[firstRouteKey];
      
      if (firstRoute && firstRoute.polyline) {
        console.log("Routes are already embedded, using directly");
        return tripData;
      }
      
      // Load each route separately
      const loadedRoutes: Record<string, any> = {};
      
      for (const routeKey of routeKeys) {
        console.log(`Fetching route data for key: ${routeKey}`);
        
        try {
          // Try direct fetch first
          let routeData = await tripApi.getTransitRoute(userId, tripId, routeKey);
          
          if (routeData) {
            console.log(`Route ${routeKey} loaded successfully`);
            console.log(routeData)
            loadedRoutes[routeKey] = routeData;
            continue;
          }
        } catch (directError) {
          console.error(`Direct fetch for ${routeKey} failed:`, directError);
          
          // Try with URL encoding if direct fetch fails
          try {
            const encodedKey = encodeURIComponent(routeKey);
            console.log(`Trying with encoded key: ${encodedKey}`);
            
            const encodedRouteData = await tripApi.getTransitRoute(userId, tripId, encodedKey);
            
            if (encodedRouteData) {
              console.log(`Route loaded with encoded key ${encodedKey}`);
              loadedRoutes[routeKey] = encodedRouteData;
              continue;
            }
          } catch (encodedError) {
            console.error(`Encoded fetch for ${routeKey} failed:`, encodedError);
          }
        }
        
        // If we get here, both fetch attempts failed
        // Keep existing reference if available
        if (tripData.transitRoutes[routeKey]) {
          console.log(`Keeping original reference for ${routeKey}`);
          loadedRoutes[routeKey] = tripData.transitRoutes[routeKey];
        }
      }
      
      // Replace references with loaded data
      tripData.transitRoutes = loadedRoutes;
      
      // Final logging
      console.log("Successfully loaded routes:", Object.keys(loadedRoutes));
      
      return tripData;
    } catch (error) {
      console.error("Error loading trip with routes:", error);
      throw error;
    }
  }
};

const secureAxios = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
});


const clearCsrfToken = () => {
  csrfTokenCache = null;
};

secureAxios.defaults.withCredentials = true;

secureAxios.interceptors.request.use(async (config) => {
  try {
    // Get CSRF token as before
    if (!csrfTokenCache) {
      console.log('Fetching new CSRF token');
      const response = await axios.create({
        baseURL: API_BASE_URL,
        withCredentials: true,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      }).get('/api/csrf-token');
      csrfTokenCache = response.data.csrfToken;
    }
    
    config.headers['X-CSRF-Token'] = csrfTokenCache;
    
    // Add Firebase token
    const user = auth.currentUser;
    if (user) {
      const idToken = await user.getIdToken();
      config.headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    return config;
  } catch (error) {
    console.error('Failed to set request headers:', error);
    clearCsrfToken();  // Clear on error
    throw error;
  }
});

// Global API error handler for limit exceeded errors
let apiErrorHandler: ((error: any, apiType?: string) => boolean) | null = null;

export const setApiErrorHandler = (handler: ((error: any, apiType?: string) => boolean) | null) => {
  apiErrorHandler = handler;
};

// Add response interceptor to handle 403 errors and API limits
secureAxios.interceptors.response.use(
  response => response,
  async error => {
    // Handle API limit errors first
    if (error.response?.status === 429 && error.response?.data?.error === 'API_LIMIT_EXCEEDED') {
      if (apiErrorHandler) {
        const handled = apiErrorHandler(error);
        if (handled) {
          // Don't retry or rethrow if the error was handled by showing the popup
          return Promise.reject(new Error('API_LIMIT_EXCEEDED'));
        }
      }
    }
    
    // Handle CSRF errors with improved retry logic
    if (error.response?.status === 403 && 
        (error.response?.data?.error?.includes('CSRF') || 
         error.response?.data?.error?.includes('Invalid CSRF token'))) {
      
      console.log('CSRF error detected, attempting token refresh and retry');
      clearCsrfToken(); // Clear invalid token
      
      // Retry the original request
      const originalRequest = error.config;
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        
        try {
          // Force fetch new CSRF token
          const response = await axios.create({
            baseURL: API_BASE_URL,
            withCredentials: true,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          }).get('/api/csrf-token');
          
          csrfTokenCache = response.data.csrfToken;
          originalRequest.headers['X-CSRF-Token'] = csrfTokenCache;
          
          console.log('New CSRF token obtained, retrying original request');
          return secureAxios(originalRequest);
        } catch (retryError) {
          console.error('Failed to refresh CSRF token:', retryError);
          return Promise.reject(error);
        }
      }
    }
    return Promise.reject(error);
  }
);

export { secureAxios };